import React from 'react'
import { cn } from '../../lib/utils'

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'destructive'
  size?: 'sm' | 'md' | 'lg'
  isLoading?: boolean
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ 
    className, 
    variant = 'primary', 
    size = 'md', 
    isLoading = false,
    leftIcon,
    rightIcon,
    children, 
    disabled,
    style,
    ...props 
  }, ref) => {
    const baseClasses = 'inline-flex items-center justify-center font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed'
    
    const variants = {
      primary: 'text-white',
      secondary: 'border',
      outline: 'border',
      ghost: 'bg-transparent',
      destructive: 'text-white'
    }
    
    const sizes = {
      sm: 'px-3 py-1.5 text-sm',
      md: 'px-4 py-2 text-sm',
      lg: 'px-6 py-3 text-base'
    }

    // Theme-aware button styles
    const getButtonStyle = (): React.CSSProperties => {
      const baseStyle = {
        '--tw-ring-color': 'var(--color-border-focus)',
        '--tw-ring-offset-color': 'var(--color-bg-primary)'
      } as any;
      
      switch (variant) {
        case 'primary':
          return {
            ...baseStyle,
            backgroundColor: 'var(--color-accent)',
            borderColor: 'var(--color-accent)',
            color: 'white',
            ...style
          };
        case 'secondary':
        case 'outline':
          return {
            ...baseStyle,
            backgroundColor: 'var(--color-bg-tertiary)',
            borderColor: 'var(--color-border)',
            color: 'var(--color-text-secondary)',
            ...style
          };
        case 'ghost':
          return {
            ...baseStyle,
            backgroundColor: 'transparent',
            color: 'var(--color-text-secondary)',
            ...style
          };
        case 'destructive':
          return {
            ...baseStyle,
            backgroundColor: 'var(--color-error)',
            borderColor: 'var(--color-error)',
            color: 'white',
            ...style
          };
        default:
          return { ...baseStyle, ...style };
      }
    };

    const handleMouseEnter = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (!disabled && !isLoading) {
        switch (variant) {
          case 'primary':
            e.currentTarget.style.backgroundColor = 'var(--color-accent-hover)';
            break;
          case 'secondary':
          case 'outline':
            e.currentTarget.style.backgroundColor = 'var(--color-bg-primary)';
            e.currentTarget.style.color = 'var(--color-text-primary)';
            break;
          case 'ghost':
            e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)';
            e.currentTarget.style.color = 'var(--color-text-primary)';
            break;
        }
      }
      props.onMouseEnter?.(e);
    };

    const handleMouseLeave = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (!disabled && !isLoading) {
        switch (variant) {
          case 'primary':
            e.currentTarget.style.backgroundColor = 'var(--color-accent)';
            break;
          case 'secondary':
          case 'outline':
            e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)';
            e.currentTarget.style.color = 'var(--color-text-secondary)';
            break;
          case 'ghost':
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = 'var(--color-text-secondary)';
            break;
        }
      }
      props.onMouseLeave?.(e);
    };

    return (
      <button
        type={props.type || 'button'}
        className={cn(
          baseClasses,
          variants[variant],
          sizes[size],
          className
        )}
        style={getButtonStyle()}
        ref={ref}
        disabled={disabled || isLoading}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        {...props}
      >
        {isLoading && (
          <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
        )}
        {!isLoading && leftIcon && <span className="mr-2">{leftIcon}</span>}
        {children}
        {!isLoading && rightIcon && <span className="ml-2">{rightIcon}</span>}
      </button>
    )
  }
)

Button.displayName = 'Button'

export { Button }
